import { DateTime } from "luxon";
import {
  CompletionScheduleInstanceStatus as Status,
  LibraryItemType,
} from "@src/types.generated";
import type { HomeForms_InstanceFragment } from "./HomeFormsCarousel.generated";

export const orderHomeForms = (
  items: HomeForms_InstanceFragment[],
  lateSubmissions: boolean,
): HomeForms_InstanceFragment[] => {
  const eligible = items.filter((item) => {
    if (item.completedBySkillInstance) return false;
    const type = item.window.completionSchedule.libraryItem.type;
    if (type === LibraryItemType.Audit) return item.isOpen;
    return (
      type === LibraryItemType.Task &&
      (item.isOpen ||
        item.status === Status.Upcoming ||
        (lateSubmissions && item.status === Status.PastDue))
    );
  });
  // Past due first, then open, then upcoming; each by its next cutoff.
  const rank = (item: HomeForms_InstanceFragment) =>
    item.status === Status.PastDue ? 0 : item.isOpen ? 1 : 2;
  const cutoff = (item: HomeForms_InstanceFragment) =>
    Date.parse(
      item.status === Status.PastDue
        ? item.lateDeadline ?? item.end
        : item.isOpen
          ? item.end
          : item.start,
    );
  const sorted = eligible.sort(
    (a, b) => rank(a) - rank(b) || cutoff(a) - cutoff(b),
  );
  // A recurring schedule has one upcoming window per period, and only the
  // soonest can be started early. Like mobile, show at most that one per
  // checklist and location, and none when that checklist already has an
  // open or past-due tile there.
  const key = (item: HomeForms_InstanceFragment) =>
    `${item.window.completionSchedule.libraryItem.id}:${item.location.id}`;
  const shown = new Set(sorted.filter((item) => rank(item) !== 2).map(key));
  return sorted.filter((item) => {
    if (rank(item) !== 2) return true;
    if (shown.has(key(item))) return false;
    shown.add(key(item));
    return true;
  });
};

export const homeFormsTitle = (items: HomeForms_InstanceFragment[]): string => {
  const hasAudits = items.some(
    (item) =>
      item.window.completionSchedule.libraryItem.type === LibraryItemType.Audit,
  );
  const hasChecklists = items.some(
    (item) =>
      item.window.completionSchedule.libraryItem.type === LibraryItemType.Task,
  );
  return hasAudits
    ? hasChecklists
      ? "Checklists & Audits"
      : "Audits"
    : "Checklists";
};

export type HomeFormTimingTone = "pastDue" | "timeRemaining" | "due";

// Matches the mobile Home task list item: Past due, a countdown or due day
// while open, else the window time today, Tomorrow, or the start date.
// Later-day labels also carry the window's start time.
export function homeFormTiming(
  item: HomeForms_InstanceFragment,
  zone: string,
  now: number,
): { tone: HomeFormTimingTone; label: string } {
  const today = DateTime.fromMillis(now, { zone });
  const start = DateTime.fromISO(item.start, { zone });
  const end = DateTime.fromISO(item.end, { zone });
  const startDay = start.hasSame(today, "day")
    ? ""
    : start.hasSame(today.minus({ days: 1 }), "day")
      ? "Yesterday"
      : start.hasSame(today.plus({ days: 1 }), "day")
        ? "Tomorrow"
        : start.toFormat("MMM d");
  const startTime = [startDay, start.toFormat("h:mm a")]
    .filter(Boolean)
    .join(", ");
  if (item.status === Status.PastDue)
    return { tone: "pastDue", label: "Past due" };
  if (item.isOpen) {
    const label = dueLabel(end, today);
    if (label) return { tone: "timeRemaining", label };
  }
  return {
    tone: "due",
    label: startDay
      ? startTime
      : `${start.toFormat("h:mm a")} - ${end.toFormat("h:mm a")}`,
  };
}

// Port of mobile's formatSubmissionDueDate.
function dueLabel(due: DateTime, now: DateTime): string {
  // A midnight deadline belongs to the previous day.
  const deadline = due.equals(due.startOf("day"))
    ? due.minus({ seconds: 1 })
    : due;
  const minutesUntilDue = deadline.diff(now, "minutes").minutes;
  if (minutesUntilDue >= 0 && minutesUntilDue < 24 * 60) {
    const minutes = Math.min(Math.ceil(minutesUntilDue), 24 * 60 - 1);
    return minutes > 60
      ? `${Math.floor(minutes / 60)}h ${minutes % 60}min to complete`
      : `${minutes} min to complete`;
  }
  if (deadline.hasSame(now, "day")) return "";
  if (deadline.hasSame(now.plus({ days: 1 }), "day")) return "Due Tomorrow";
  const startOfWeek = now.startOf("week");
  if (
    deadline >= startOfWeek &&
    deadline <= startOfWeek.plus({ days: 6 }).endOf("day")
  )
    return `Due ${deadline.toFormat("EEEE")}`;
  const days = deadline.startOf("day").diff(now.startOf("day"), "days").days;
  return days <= 14
    ? `Due ${deadline.toFormat("ccc M/d")}`
    : `Due ${deadline.toFormat("M/d/yyyy")}`;
}
